import { z } from "zod";
import { timestampsSchema } from "./common";
import { listQueryBaseSchema } from "./list-query";
import { CLIENT_STATUS } from "../constants/status";

const statusValues = Object.values(CLIENT_STATUS) as [number, ...number[]];

const clientStatusSchema = z.union([
  z.literal(statusValues[0]),
  ...statusValues.slice(1).map((v) => z.literal(v)),
]);

/**
 * Whether the client's email has a sign-in account, and whether that account
 * has confirmed its email. Web sign-up creates the account before the email is
 * checked, so "unverified" rows can hold a made-up address.
 */
export const clientAccountStatusSchema = z.enum(["none", "unverified", "verified"]);
export type ClientAccountStatus = z.infer<typeof clientAccountStatusSchema>;

/**
 * "client": a row in `clients` (made when someone books, or by the team).
 * "signup": a website/app account that never booked, so it has no client row
 * yet. Its id is the user id, and the client-only actions (edit, ban, delete)
 * do not apply to it.
 */
export const clientKindSchema = z.enum(["client", "signup"]);
export type ClientKind = z.infer<typeof clientKindSchema>;

export const clientDtoSchema = z
  .object({
    id: z.string().uuid(),
    kind: clientKindSchema.optional(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    whatsappNumber: z.string(),
    /** True only once the account has confirmed its email. */
    hasAccount: z.boolean().optional(),
    accountStatus: clientAccountStatusSchema.optional(),
    status: clientStatusSchema.nullable(),
    address: z.string().nullable(),
    banReason: z.string().nullable(),
    reservationsCount: z.number().int().nonnegative().optional(),
    totalSpent: z.number().nonnegative().optional(),
    lastBookingAt: z.string().nullable().optional(),
  })
  .extend(timestampsSchema.shape);
export type ClientDTO = z.infer<typeof clientDtoSchema>;

export const clientUpsertSchema = z.object({
  firstName: z.string().min(1),
  // Optional: walk-in / WhatsApp clients often share only a single name.
  lastName: z.string().optional(),
  // Optional: customers booking by WhatsApp often don't share an email.
  // Accept empty string OR a valid email OR omitted.
  email: z.union([z.literal(""), z.email()]).optional(),
  whatsappNumber: z.string().min(1),
  address: z.string().optional(),
});
export type ClientUpsertInput = z.infer<typeof clientUpsertSchema>;

export const clientBanSchema = z.object({
  reason: z.string().min(1),
});
export type ClientBanInput = z.infer<typeof clientBanSchema>;

export const clientsListQuerySchema = listQueryBaseSchema.extend({
  status: z.coerce.number().int().optional(),
  /** "verified": only clients whose email has a confirmed website/app account. */
  accountStatus: z.enum(["verified"]).optional(),
  /** Omitted: clients and sign-ups together. */
  kind: clientKindSchema.optional(),
});
export type ClientsListQuery = z.infer<typeof clientsListQuerySchema>;
